import { z } from 'zod';
import { CrmConversationMessageDto } from './messaging';

export const CrmTelegramAuthStartBody = z.object({
  phone: z.string().trim().min(8).max(32),
  label: z.string().trim().min(1).max(120),
  cityIds: z.array(z.uuid()).min(1).max(20),
});
export type CrmTelegramAuthStartBody = z.infer<typeof CrmTelegramAuthStartBody>;

export const CrmTelegramAuthStartDto = z.object({
  authSessionId: z.uuid(),
  channelAccountId: z.uuid(),
});
export type CrmTelegramAuthStartDto = z.infer<typeof CrmTelegramAuthStartDto>;

export const CrmTelegramAuthCodeBody = z.object({
  authSessionId: z.uuid(),
  code: z.string().trim().min(4).max(12),
});
export type CrmTelegramAuthCodeBody = z.infer<typeof CrmTelegramAuthCodeBody>;

export const CrmTelegramAuthPasswordBody = z.object({
  authSessionId: z.uuid(),
  password: z.string().min(1).max(256),
});
export type CrmTelegramAuthPasswordBody = z.infer<typeof CrmTelegramAuthPasswordBody>;

export const CrmTelegramAuthStepDto = z.discriminatedUnion('step', [
  z.object({ step: z.literal('awaiting_code') }),
  z.object({ step: z.literal('awaiting_password') }),
  z.object({
    step: z.literal('connected'),
    channelAccountId: z.uuid(),
  }),
]);
export type CrmTelegramAuthStepDto = z.infer<typeof CrmTelegramAuthStepDto>;

export const CrmTelegramAccountDto = z.object({
  id: z.uuid(),
  label: z.string(),
  externalAccountId: z.string(),
  status: z.enum(['CONNECTING', 'ACTIVE', 'DISABLED', 'ERROR', 'REAUTH_REQUIRED']),
  cityIds: z.array(z.uuid()),
  responsibleUserId: z.uuid().nullable(),
  responsibleDisplayName: z.string().min(1).max(120).nullable(),
  createdAt: z.iso.datetime(),
});
export type CrmTelegramAccountDto = z.infer<typeof CrmTelegramAccountDto>;

export const CrmTelegramReassignResponsibleBody = z.object({
  responsibleUserId: z.uuid().nullable(),
});
export type CrmTelegramReassignResponsibleBody = z.infer<typeof CrmTelegramReassignResponsibleBody>;

export const CrmTelegramAccountsDto = z.object({
  items: z.array(CrmTelegramAccountDto),
});
export type CrmTelegramAccountsDto = z.infer<typeof CrmTelegramAccountsDto>;

export const CrmSendConversationMessageBody = z.object({
  body: z.string().trim().min(1).max(20_000),
  /** Последнее сообщение в треде на момент отправки (для защиты от гонки). */
  baselineMessageId: z.uuid().optional(),
  /** Повторная отправка после конфликта «коллега уже ответил». */
  forceSend: z.boolean().optional(),
  /** Идempotency: повтор POST с тем же ключом не дублирует сообщение. */
  clientSendId: z.uuid().optional(),
});
export type CrmSendConversationMessageBody = z.infer<typeof CrmSendConversationMessageBody>;

export const CrmSendConversationMessageDto = z.object({
  messageId: z.uuid(),
  status: z.enum(['QUEUED', 'SENT', 'FAILED']),
});
export type CrmSendConversationMessageDto = z.infer<typeof CrmSendConversationMessageDto>;

export const CrmSendConversationConflictDto = z.object({
  code: z.literal('colleague_replied'),
  colleagueMessage: CrmConversationMessageDto,
  draftBody: z.string(),
});
export type CrmSendConversationConflictDto = z.infer<typeof CrmSendConversationConflictDto>;

export const CrmConversationPresenceParticipantDto = z.object({
  userId: z.uuid(),
  displayName: z.string().min(1).max(120),
  viewing: z.boolean(),
  typing: z.boolean(),
});
export type CrmConversationPresenceParticipantDto = z.infer<typeof CrmConversationPresenceParticipantDto>;

export const CrmConversationPresenceDto = z.object({
  conversationId: z.uuid(),
  participants: z.array(CrmConversationPresenceParticipantDto),
});
export type CrmConversationPresenceDto = z.infer<typeof CrmConversationPresenceDto>;

export const CrmConversationPresenceBody = z.object({
  viewing: z.boolean().optional(),
  typing: z.boolean().optional(),
});
export type CrmConversationPresenceBody = z.infer<typeof CrmConversationPresenceBody>;
